import type { PaymentHistoryViewDto } from '@subscriptions/application/dtos';
import { InvoiceStatuses } from '@subscriptions/domain/types/SubscriptionsConst';
import { buildInvoiceDataRetryPath } from '@subscriptions/presentation/utils/invoiceDataRetryPath';
import { isPaymentFromCurrentMonth } from '@subscriptions/presentation/utils/isPaymentFromCurrentMonth';

/** What a payment offers for its CFDI, once every gate has been applied */
export type PaymentInvoiceActions = {
  canDownload: boolean;
  /** The CFDI is stamped but its files are still being fetched */
  isPreparingFiles: boolean;
  /** Deep link to the retry sheet, or null when the retry is not on offer */
  retryPath: string | null;
  /** A collapsed parent flags what failed one level below */
  hasRetryableChildren: boolean;
};

/**
 * Reads a payment's invoicing outcome, tolerating an API that does not report it yet: a payment
 * carrying files is read as ISSUED, which is what their presence meant before the field existed.
 *
 * @param payment - Payment as the view DTO carries it
 * @returns The reported status, the inferred one, or null when there is no invoice
 */
export const readInvoiceStatus = (payment: PaymentHistoryViewDto): string | null => {
  if (payment.invoiceStatus !== null) return payment.invoiceStatus;
  return payment.pdfUrl || payment.xmlUrl ? InvoiceStatuses.ISSUED : null;
};

/**
 * Resolves what a payment offers for its CFDI: download, wait, or retry.
 *
 * Shared by the desktop table row and the mobile detail sheet so both answer with the same rule.
 * The actions belong to whichever row holds the CFDI, so a parent yields as soon as a child
 * reports one of its own.
 *
 * @param params.payment - Payment being rendered
 * @param params.blockUuid - Block the view loaded, when it has one
 * @param params.isChild - Per-store row, which narrows the retry to its own CFDI
 * @returns The actions this payment offers
 */
export const resolvePaymentInvoiceActions = ({
  payment,
  blockUuid,
  isChild = false,
}: {
  payment: PaymentHistoryViewDto;
  blockUuid?: string;
  isChild?: boolean;
}): PaymentInvoiceActions => {
  const childrenOwningInvoice = (payment.relatedInvoices ?? []).filter((child) => readInvoiceStatus(child) !== null);
  const invoiceStatus = readInvoiceStatus(payment);
  const ownsInvoice = invoiceStatus !== null && childrenOwningInvoice.length === 0;
  const hasFiles = Boolean(payment.pdfUrl || payment.xmlUrl);
  const hasFailedInvoice = ownsInvoice && invoiceStatus === InvoiceStatuses.FAILED;
  const retryBlockUuid = blockUuid ?? payment.blockUuid;
  const canRetryInvoices =
    isPaymentFromCurrentMonth(payment.createdAt) && Boolean(retryBlockUuid) && Boolean(payment.invoiceDataUuid);

  return {
    canDownload: ownsInvoice && invoiceStatus === InvoiceStatuses.ISSUED && hasFiles,
    isPreparingFiles: ownsInvoice && invoiceStatus === InvoiceStatuses.ISSUED && !hasFiles,
    hasRetryableChildren:
      canRetryInvoices && childrenOwningInvoice.some((child) => child.invoiceStatus === InvoiceStatuses.FAILED),
    retryPath:
      hasFailedInvoice && canRetryInvoices && retryBlockUuid && payment.invoiceDataUuid
        ? buildInvoiceDataRetryPath({
            blockUuid: retryBlockUuid,
            errorMessage: payment.errorMessage,
            invoiceDataUuid: payment.invoiceDataUuid,
            onlyFailed: isChild,
            paymentUuid: payment.paymentUuid,
          })
        : null,
  };
};
